import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { registerAction, verifyOtpAction, getProfileDataAction } from '../redux/auth/auth.actions';
import { toast } from 'react-toastify';

export default function VerifyCode() {
  const length = 6;
  const [otp, setOtp] = useState(new Array(length).fill(''));
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);
  const dispatch = useDispatch();
  const { verifyOtp, isLoading } = useSelector((state) => state.authInfo);
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState("");

  useEffect(() => {
    setCurrentLanguage(localStorage.getItem("language"));
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    if (verifyOtp?.status === 201) {
      setIsVerifying(false);
      const successMessage = currentLanguage === "Italian"
        ? "Registrazione completata con successo"
        : "Registration successful";
      toast.success(successMessage);
      localStorage.removeItem('registerData');
      // Refresh profile data after successful registration to ensure phone number is loaded
      dispatch(getProfileDataAction());
      navigate('/auth');
    } else if (verifyOtp?.error) {
      setIsVerifying(false);
      toast.error(verifyOtp.error || (currentLanguage === "Italian" ? 'Verifica fallita. Per favore riprova.' : 'Verification failed. Please try again.'));
    }
  }, [verifyOtp, navigate, currentLanguage, dispatch]);

  const fillOtpFromDigits = (digits) => {
    const newOtp = new Array(length).fill('');
    digits.slice(0, length).forEach((digit, idx) => {
      newOtp[idx] = digit;
    });
    setOtp(newOtp);

    const focusIndex = Math.min(digits.length, length - 1);
    if (inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex].focus();
    }
  };

  const handleChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (!value) return;

    if (value.length > 1) {
      fillOtpFromDigits(value.split(''));
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value.charAt(0);
    setOtp(newOtp);

    if (index < length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedDigits = e.clipboardData.getData('text/plain').replace(/\D/g, '');
    if (!pastedDigits) return;
    fillOtpFromDigits(pastedDigits.split(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = '';
      } else if (index > 0) {
        newOtp[index - 1] = '';
        inputRefs.current[index - 1].focus();
      }
      setOtp(newOtp);
    }
  };

  const handleResendOtp = async () => {
    try {
      setIsResending(true);
      // Clear OTP fields
      setOtp(new Array(length).fill(''));
      // Focus on first input
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
      dispatch(registerAction(JSON.parse(localStorage.getItem("registerData"))));
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success(currentLanguage === "Italian" ? 'OTP reinviato con successo' : 'OTP resent successfully');
    } catch (error) {
      toast.error(currentLanguage === "Italian" ? 'Impossibile reinviare OTP. Per favore riprova.' : 'Failed to resend OTP. Please try again.');
      console.error('Resend OTP error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = () => {
    try {
      setIsVerifying(true);
      const otpString = otp.join('');

      if (otpString.length !== length) {
        setIsVerifying(false);
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
        return toast.error(currentLanguage === "Italian" ? 'Per favore inserisci il codice di verifica completo' : 'Please enter the complete verification code');
      }

      const registerData = JSON.parse(localStorage.getItem('registerData'));
      if (!registerData) {
        setIsVerifying(false);
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
        return toast.error(currentLanguage === "Italian" ? 'Dati di registrazione non trovati. Per favore registrati di nuovo.' : 'Registration data not found. Please register again.');
      }
      dispatch(verifyOtpAction({ ...registerData, otp: otpString }));
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(currentLanguage === "Italian" ? 'Si è verificato un errore durante la verifica' : 'An error occurred during verification');
      setIsVerifying(false);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  };

  return (
    <div className="verify_code">
      <div className="top_text">
        {currentLanguage === "Italian" ? 'Inserisci il codice di verifica a 6 cifre inviato alla tua email.' : 'Enter 6-digit verification code sent to your email.'}
      </div>
      <div className="otp_container" onPaste={handlePaste}>
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            ref={(el) => (inputRefs.current[index] = el)}
            className="otp_input"
            placeholder="•"
            disabled={isLoading}
          />
        ))}
      </div>
      <div className="action mb15">
        <button
          onClick={handleVerify}
          disabled={isLoading}
          className={isLoading ? "loading-btn" : ""}
        >
          {isLoading ? (
            <>
              <span className="loading-spinner"></span>
              <span>{currentLanguage === "Italian" ? 'Verifica in corso...' : 'Verifying...'}</span>
            </>
          ) : (currentLanguage === "Italian" ? 'Verifica' : 'Verify')}
        </button>
      </div>
      <div className="have_account">
        {currentLanguage === "Italian" ? 'Non hai ricevuto?' : "Didn't receive?"} <button onClick={handleResendOtp} disabled={isLoading || isResending}>
          {isResending ? (currentLanguage === "Italian" ? 'Reinvio in corso...' : 'Resending...') : (currentLanguage === "Italian" ? 'Reinvia' : 'Resend')}
        </button>
      </div>
    </div>
  );
}
